import React, { useState } from 'react';
import { ReviewCard } from './ReviewCard';
import { useReviewQueue } from '../hooks/useReviewQueue';

/**
 * ReviewQueueSection - Review queue for spaced repetition
 */
export function ReviewQueueSection({ initialCount = 0, isVisible = true }) {
  const [expanded, setExpanded] = useState(false);
  const {
    items,
    totalDue,
    isLoading,
    isSubmitting,
    error,
    submitGrade,
    refetch,
  } = useReviewQueue({ enabled: expanded, limit: 10 });

  // Use initial count from insights data, update when queue is fetched
  const displayCount = expanded ? totalDue : initialCount;

  // If no items due, show minimal state
  if (!expanded && displayCount === 0) {
    return (
      <div style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-panel)',
        padding: 'var(--space-5)',
        marginTop: 'var(--space-5)',
      }}>
        <div style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-tertiary)',
          marginBottom: 'var(--space-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Review Queue
        </div>
        <div style={{
          fontSize: 13,
          color: 'var(--text-tertiary)',
          textAlign: 'center',
          padding: 'var(--space-5) 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          All caught up! No reviews due.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-panel)',
      padding: 'var(--space-5)',
      marginTop: 'var(--space-5)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: expanded ? 'var(--space-4)' : 0,
      }}>
        <div style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-tertiary)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Review Queue
          {displayCount > 0 && (
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-control)',
              background: 'var(--warning-bg)',
              color: 'var(--warning)',
              textTransform: 'none',
              letterSpacing: 0,
            }}>
              {displayCount} due
            </span>
          )}
        </div>

        {!expanded && displayCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 'var(--radius-control)',
              background: 'var(--accent-primary)',
              color: 'var(--text-inverted)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            Start Review
          </button>
        )}

        {expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 'var(--radius-control)',
              background: 'var(--glass-bg-medium)',
              color: 'var(--text-secondary)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Collapse
          </button>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <>
          {isLoading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--space-7)',
              gap: 'var(--space-3)',
            }}>
              <div style={{
                width: 24,
                height: 24,
                border: '2px solid var(--border-subtle)',
                borderTopColor: 'var(--accent-primary)',
                borderRadius: '50%',
                animation: 'graspd-spin 0.8s linear infinite',
              }} />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading reviews...</span>
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: 'var(--space-5)',
            }}>
              <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 'var(--space-3)' }}>{error}</p>
              <button
                type="button"
                onClick={refetch}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 'var(--radius-control)',
                  background: 'var(--accent-primary)',
                  color: 'var(--text-inverted)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
            </div>
          ) : items.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 'var(--space-7)',
              fontSize: 13,
              color: 'var(--text-tertiary)',
            }}>
              <div style={{ fontSize: 32, marginBottom: 'var(--space-3)' }}>🎉</div>
              All done! No more reviews pending.
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
            }}>
              {items.map((item) => (
                <ReviewCard
                  key={item.conceptId}
                  conceptId={item.conceptId}
                  courseId={item.courseId}
                  conceptName={item.conceptName}
                  retrievability={item.retrievability}
                  status={item.status}
                  priority={item.priority}
                  lastReviewAt={item.lastReviewAt}
                  nextReviewAt={item.nextReviewAt}
                  onGrade={submitGrade}
                  isSubmitting={isSubmitting}
                />
              ))}

              {totalDue > items.length && (
                <div style={{
                  textAlign: 'center',
                  padding: 'var(--space-3) 0',
                  fontSize: 12,
                  color: 'var(--text-tertiary)',
                }}>
                  +{totalDue - items.length} more items in queue
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
